import React, { useMemo, useState } from 'react'
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  HStack,
  Spinner,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { MdMap, MdFormatListBulleted } from 'react-icons/md'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getRequest, postRequest } from 'utils/http'
import VisualSeatPicker from './VisualSeatPicker'

const seatMapKey = eventId => ['adminGuestSeatMap', eventId]

const getSeatGuid = (seat, row) =>
  seat.seat_guid || seat.uuid || `seat-${row.row_number}-${seat.seat_number}`

const fetchSeatMap = async eventId => {
  const response = await getRequest(`events/${eventId}/seat-map`)
  return response.data
}

const updateReservations = ({ eventId, action, seatGuids }) =>
  postRequest(`events/${eventId}/guest-seat-reservations`, {
    action,
    seat_guids: seatGuids,
  })

export default function GuestSeatReservations({
  eventId,
  planJson,
  ticketTiers = [],
  bookingStartDatetime,
  draftGuestSeatGuids = [],
  onDraftChange,
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [selectedGuids, setSelectedGuids] = useState([])
  const [viewMode, setViewMode] = useState('map')
  const isDraft = !eventId
  const { data, isLoading, isError, error } = useQuery({
    queryKey: seatMapKey(eventId),
    queryFn: () => fetchSeatMap(eventId),
    enabled: Boolean(eventId),
  })
  const reservationMutation = useMutation({
    mutationFn: updateReservations,
    onSuccess: (_, variables) => {
      setSelectedGuids([])
      queryClient.invalidateQueries({ queryKey: seatMapKey(variables.eventId) })
      toast({
        title: variables.action === 'reserve' ? 'Seats reserved for guests' : 'Seats released',
        description: variables.action === 'reserve'
          ? 'No ticket order or payment was created.'
          : 'These seats are available for public booking again.',
        status: 'success',
        duration: 3500,
        isClosable: true,
      })
    },
    onError: mutationError => {
      toast({
        title: 'Could not update seats',
        description: mutationError?.response?.data?.message || mutationError?.message || 'Please try again.',
        status: 'error',
        duration: 4500,
        isClosable: true,
      })
    },
  })

  const activePlanJson = isDraft ? planJson : data?.plan_json
  const ticketTiersForPicker = data?.ticket_tiers?.length ? data.ticket_tiers : ticketTiers
  const seatCategories = useMemo(() => {
    const categories = new Map()
    ;(activePlanJson?.categories || []).forEach(category => {
      if (category?.name) {
        categories.set(category.name, { name: category.name, color: category.color || '#0756C9' })
      }
    })
    ticketTiersForPicker.forEach(tier => {
      if (tier?.name) {
        categories.set(tier.name, {
          name: tier.name,
          color: tier.color || categories.get(tier.name)?.color || '#0756C9',
        })
      }
    })
    return Array.from(categories.values())
  }, [activePlanJson?.categories, ticketTiersForPicker])

  const rows = useMemo(() => {
    const statusMap = data?.seat_statuses || {}
    return (activePlanJson?.zones || []).flatMap((zone, zoneIndex) =>
      (zone.rows || [])
        .map((row, rowIndex) => ({
          key: `${zoneIndex}-${rowIndex}`,
          zoneName: zone.name || zone.label || `Zone ${zoneIndex + 1}`,
          rowName: row.row_label || row.row_number || `Row ${rowIndex + 1}`,
          seats: (row.seats || []).map(seat => {
            const guid = getSeatGuid(seat, row)
            return {
              guid,
              number: seat.seat_number,
              status: isDraft
                ? (draftGuestSeatGuids.includes(guid) ? 'draft_reserved' : 'available')
                : (statusMap[guid]?.status || 'available'),
            }
          }),
        }))
        .filter(row => row.seats.length > 0)
    )
  }, [activePlanJson, data?.seat_statuses, draftGuestSeatGuids, isDraft])

  const selectedSeats = rows.flatMap(row => row.seats).filter(seat => selectedGuids.includes(seat.guid))
  const selectedAvailable = selectedSeats.filter(seat => seat.status === 'available').map(seat => seat.guid)
  const selectedReserved = selectedSeats.filter(seat => seat.status === 'unavailable').map(seat => seat.guid)
  const busy = reservationMutation.isPending

  const toggleSeat = seat => {
    if (busy) return

    if (isDraft) {
      if (seat.status === 'draft_reserved') {
        onDraftChange?.(draftGuestSeatGuids.filter(guid => guid !== seat.guid))
      } else if (seat.status === 'available') {
        onDraftChange?.([...draftGuestSeatGuids, seat.guid])
      }
      return
    }

    if (!['available', 'unavailable'].includes(seat.status)) return
    setSelectedGuids(current => current.includes(seat.guid)
      ? current.filter(guid => guid !== seat.guid)
      : [...current, seat.guid])
  }

  const saveSelection = action => {
    const seatGuids = action === 'reserve' ? selectedAvailable : selectedReserved
    if (!seatGuids.length) return
    reservationMutation.mutate({ eventId, action, seatGuids })
  }

  if (!isDraft && isLoading) {
    return <Flex justify="center" py={8}><Spinner /></Flex>
  }

  if (!isDraft && isError) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Text>{error?.response?.data?.message || error?.message || 'Could not load the event seat map.'}</Text>
      </Alert>
    )
  }

  if (!activePlanJson?.zones?.length || rows.length === 0) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Text>This seating plan has no individual seats to reserve. Add seats to its rows first.</Text>
      </Alert>
    )
  }

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={{ base: 4, md: 5 }} _dark={{ borderColor: 'gray.700' }}>
      <Flex justify="space-between" align={{ base: 'start', md: 'center' }} gap={3} wrap="wrap" mb={3}>
        <Box>
          <HStack spacing={2} mb={1}>
            <Heading size="sm">Guest seat reservations</Heading>
            <Badge colorScheme="purple">No payment</Badge>
          </HStack>
          <Text fontSize="sm" color="gray.500">
            Select among {rows.reduce((total, row) => total + row.seats.length, 0)} seats; guest reservations never create an order or payment.
          </Text>
        </Box>
        <HStack spacing={2}>
          {busy && <Spinner size="sm" />}
          <ButtonGroup size="xs" isAttached variant="outline">
            <Button
              leftIcon={<MdMap />}
              colorScheme={viewMode === 'map' ? 'purple' : 'gray'}
              variant={viewMode === 'map' ? 'solid' : 'outline'}
              onClick={() => setViewMode('map')}
            >
              Visual Map View
            </Button>
            <Button
              leftIcon={<MdFormatListBulleted />}
              colorScheme={viewMode === 'list' ? 'purple' : 'gray'}
              variant={viewMode === 'list' ? 'solid' : 'outline'}
              onClick={() => setViewMode('list')}
            >
              Row List View
            </Button>
          </ButtonGroup>
        </HStack>
      </Flex>

      {isDraft ? (
        <Alert status="info" borderRadius="md" mb={4}>
          <AlertIcon />
          <Text fontSize="sm">Choose seats now on the venue map. They will be locked for this event when you save it.</Text>
        </Alert>
      ) : (
        <Alert status="info" borderRadius="md" mb={4}>
          <AlertIcon />
          <Text fontSize="sm">You can reserve guest seats or release previously reserved seats directly on the interactive seating map below.</Text>
        </Alert>
      )}

      <HStack spacing={4} mb={3} fontSize="xs" color="gray.600" flexWrap="wrap">
        {seatCategories.map(category => (
          <HStack key={category.name}>
            <Box w={3} h={3} borderRadius="full" bg={category.color} />
            <Text>{category.name} · Available</Text>
          </HStack>
        ))}
        <HStack>
          <Box w={3} h={3} borderRadius="full" bg="white" border="2px solid" borderColor={seatCategories[0]?.color || '#0756C9'} />
          <Text>{isDraft ? 'Selected for this event' : 'Selected'}</Text>
        </HStack>
        {!isDraft && <HStack><Flex w={4} h={4} borderRadius="full" bg="#94A3B8" color="#334155" fontSize="9px" fontWeight="bold" align="center" justify="center">B</Flex><Text>B · Guest reserved / blocked · Click to release</Text></HStack>}
        {!isDraft && <HStack><Box w={3} h={3} borderRadius="full" bg="#E2E8F0" /><Text>Sold / Booked · no label</Text></HStack>}
        {!isDraft && <HStack><Flex w={4} h={4} borderRadius="full" bg="#CBD5E1" color="#334155" fontSize="9px" fontWeight="bold" align="center" justify="center">H</Flex><Text>Buyer hold</Text></HStack>}
      </HStack>

      {viewMode === 'map' ? (
        <VisualSeatPicker
          planJson={activePlanJson}
          ticketTiers={ticketTiersForPicker}
          seatStatuses={data?.seat_statuses || {}}
          selectedGuids={isDraft ? draftGuestSeatGuids : selectedGuids}
          onToggleSeat={toggleSeat}
          isDraft={isDraft}
          adminReservationMode={!isDraft}
          disabled={busy}
        />
      ) : (
        <VStack align="stretch" spacing={2} maxH="460px" overflowY="auto" pr={1}>
          {rows.map(row => (
            <Flex key={row.key} align="center" gap={3} py={2} px={2} borderRadius="md" bg="gray.50" _dark={{ bg: 'whiteAlpha.50' }} wrap="wrap">
              <Text minW="110px" fontSize="xs" fontWeight="bold" color="gray.600" _dark={{ color: 'gray.300' }}>
                {row.zoneName} · {row.rowName}
              </Text>
              <HStack spacing={1} flexWrap="wrap">
                {row.seats.map(seat => {
                  const selected = isDraft
                    ? seat.status === 'draft_reserved'
                    : selectedGuids.includes(seat.guid)
                  const status = seat.status
                  const selectable = isDraft
                    ? status === 'draft_reserved' || status === 'available'
                    : status === 'unavailable' || status === 'available'
                  const isBooked = status === 'booked' || status === 'sold'
                  const isHeld = status === 'held' || status === 'hold'
                  const isGuestReserved = status === 'unavailable' || status === 'reserved'
                  const label = status === 'draft_reserved'
                    ? 'Selected for guest reservation'
                    : isGuestReserved
                      ? 'Guest reserved · Blocked (click to release)'
                      : isBooked
                        ? 'Sold / Booked'
                        : isHeld
                          ? 'Buyer hold'
                          : 'Available'
                  const seatLabel = isDraft
                    ? seat.number
                    : isBooked
                      ? ''
                      : isHeld
                        ? 'H'
                        : isGuestReserved
                          ? 'B'
                          : selected ? '✓' : seat.number
                  return (
                    <Tooltip key={seat.guid} label={`${row.zoneName} · ${row.rowName} · Seat ${seat.number} · ${label}`}>
                      <Button
                        size="xs"
                        minW="32px"
                        h="30px"
                        px={1.5}
                        bg={!isDraft && isGuestReserved ? '#94A3B8' : !isDraft && isHeld ? '#CBD5E1' : !isDraft && isBooked ? '#E2E8F0' : undefined}
                        color={!isDraft && (isBooked || isHeld || isGuestReserved) ? '#334155' : undefined}
                        borderColor={!isDraft && isGuestReserved ? '#94A3B8' : !isDraft && isHeld ? '#CBD5E1' : !isDraft && isBooked ? '#E2E8F0' : undefined}
                        colorScheme={selected ? 'purple' : 'green'}
                        variant={!isDraft && (isBooked || isHeld || isGuestReserved) ? 'solid' : selected ? 'solid' : 'outline'}
                        boxShadow={selected && isGuestReserved ? '0 0 0 2px #64748B' : undefined}
                        isDisabled={!selectable || busy}
                        aria-pressed={selected}
                        onClick={() => toggleSeat(seat)}
                      >
                        {seatLabel}
                      </Button>
                    </Tooltip>
                  )
                })}
              </HStack>
            </Flex>
          ))}
        </VStack>
      )}

      <Flex justify="space-between" align="center" mt={4} gap={3} wrap="wrap">
        <Text fontSize="sm" color="gray.500">
          {isDraft
            ? `${draftGuestSeatGuids.length} seat(s) will be reserved when you save this event.`
            : selectedGuids.length
              ? `${selectedGuids.length} seat(s) selected`
              : 'Select available seats to reserve, or gray seats to release.'}
        </Text>
        {isDraft ? (
          <Button size="sm" variant="outline" isDisabled={!draftGuestSeatGuids.length} onClick={() => onDraftChange?.([])}>
            Clear selection
          </Button>
        ) : (
          <HStack>
            <Button
              size="sm"
              colorScheme="purple"
              isDisabled={!selectedAvailable.length || busy}
              isLoading={busy && reservationMutation.variables?.action === 'reserve'}
              onClick={() => saveSelection('reserve')}
            >
              Reserve selected ({selectedAvailable.length})
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorScheme="gray"
              isDisabled={!selectedReserved.length || busy}
              isLoading={busy && reservationMutation.variables?.action === 'release'}
              onClick={() => saveSelection('release')}
            >
              Release selected ({selectedReserved.length})
            </Button>
          </HStack>
        )}
      </Flex>
    </Box>
  )
}
